<template>
    <div class="map-search-container">
        <div class="search-input-wrapper">
            <InputText v-model="searchQuery" placeholder="Search regions or constituencies..." class="search-input"
                @input="handleSearch" @focus="showDropdown = true" @blur="handleBlur" />
            <Button v-if="searchQuery" icon="pi pi-times" class="clear-button" text @click="clearSearch" />
        </div>

        <div v-if="showDropdown && searchResults.length > 0" class="search-dropdown">
            <div v-for="result in searchResults" :key="`${result.type}-${result.id}`" class="search-result-item"
                @click="selectResult(result)" @mouseenter="highlightResult(result)">
                <div class="result-content">
                    <div class="result-name">{{ result.name }}</div>
                    <div class="result-type">
                        <i :class="result.type === 'region' ? 'pi pi-map' : 'pi pi-building'"></i>
                        {{ result.type === 'region' ? 'Region' : 'Constituency' }}
                        <span v-if="result.region" class="result-region">({{ result.region }})</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

interface SearchResult {
    id: number
    name: string
    type: 'region' | 'constituency'
    region?: string
}

interface Props {
    searchResults: SearchResult[]
    onSearch: (query: string) => void
    onSelect: (result: SearchResult) => void
    onHighlight: (result: SearchResult) => void
    onClear: () => void
}

const props = defineProps<Props>()

const searchQuery = ref('')
const showDropdown = ref(false)

const handleSearch = () => {
    props.onSearch(searchQuery.value)
}

const selectResult = (result: SearchResult) => {
    props.onSelect(result)
    searchQuery.value = result.name
    showDropdown.value = false
}

const highlightResult = (result: SearchResult) => {
    props.onHighlight(result)
}

const clearSearch = () => {
    searchQuery.value = ''
    props.onClear()
    showDropdown.value = false
}

const handleBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => {
        showDropdown.value = false
    }, 200)
}

// Watch for external changes to search results
watch(() => props.searchResults, (newResults) => {
    if (newResults.length === 0) {
        showDropdown.value = false
    }
})
</script>

<style scoped>
.map-search-container {
    position: relative;
    width: 300px;
}

.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.search-input {
    width: 100%;
    padding-right: 40px;
}

.clear-button {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    padding: 0;
}

.search-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    max-height: 300px;
    overflow-y: auto;
    z-index: 1000;
    margin-top: 2px;
}

.search-result-item {
    padding: 12px 16px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.2s;
}

.search-result-item:hover {
    background-color: #f8f9fa;
}

.search-result-item:last-child {
    border-bottom: none;
}

.result-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.result-name {
    font-weight: 600;
    color: #333;
}

.result-type {
    font-size: 0.875rem;
    color: #666;
    display: flex;
    align-items: center;
    gap: 6px;
}

.result-region {
    color: #888;
    font-style: italic;
}

.result-type i {
    font-size: 0.75rem;
}
</style>